import type { ThirdPartyBillingAccount } from '../../services/FP_FulfillmentContextService/types';
import { extractTenantDetails, findScopedTenant, generateHydrationError, isActiveAsset } from './utils';
import type {
  HydratedThirdPartyBillingAccount,
  HydratedThirdPartyBillingAccountSet,
  HydrationAssetError,
} from '../types';
import { hydrateThirdPartyBillingAccount } from './hydrateThirdPartyBillingAccount';
import type { PartnerTenant, PartnerThirdPartyBillingAccountSet } from '@shipium-corp/shipium-ts-types';

export interface HydrateThirdPartyBillingAccountSetParams {
  thirdPartyBillingAccountSet: Required<PartnerThirdPartyBillingAccountSet>;
  thirdPartyBillingAccounts: Record<string, ThirdPartyBillingAccount>;
  tenantList: PartnerTenant[];
}

const assetType = 'thirdPartyBillingAccountSet';
const generateErrors = (
  thirdPartyBillingAccountSet: Required<PartnerThirdPartyBillingAccountSet>,
  thirdPartyBillingAccounts: HydratedThirdPartyBillingAccount[],
): HydrationAssetError[] => {
  const errors: HydrationAssetError[] = [];
  thirdPartyBillingAccounts.forEach((account) => {
    account.validationInfo?.errors.forEach((error) => {
      errors.push(error);
    });
  });

  return [
    ...(!isActiveAsset(thirdPartyBillingAccountSet)
      ? [generateHydrationError('Not active', assetType, thirdPartyBillingAccountSet.thirdPartyBillingAccountSetId)]
      : []),
    ...errors,
  ];
};

const generateWarnings = (thirdPartyBillingAccounts: HydratedThirdPartyBillingAccount[]): HydrationAssetError[] => {
  const warnings: HydrationAssetError[] = [];
  thirdPartyBillingAccounts.forEach((account) => {
    account.validationInfo?.warnings.forEach((warning) => {
      warnings.push(warning);
    });
  });
  return warnings;
};

export const hydrateThirdPartyBillingAccountSet = ({
  thirdPartyBillingAccountSet,
  thirdPartyBillingAccounts,
  tenantList,
}: HydrateThirdPartyBillingAccountSetParams): HydratedThirdPartyBillingAccountSet => {
  const foundThirdPartyBillingAccounts = thirdPartyBillingAccountSet.thirdPartyBillingAccountIds.map(
    (accountId: string) => {
      return hydrateThirdPartyBillingAccount(tenantList, thirdPartyBillingAccounts[accountId]);
    },
  );
  const tenant = findScopedTenant(
    thirdPartyBillingAccountSet.thirdPartyBillingAccountSetId,
    'thirdPartyBillingAccountSet',
    tenantList,
  );
  const errors = generateErrors(thirdPartyBillingAccountSet, foundThirdPartyBillingAccounts);
  const warnings = generateWarnings(foundThirdPartyBillingAccounts);

  return {
    ...thirdPartyBillingAccountSet,
    ...(foundThirdPartyBillingAccounts?.length > 0
      ? {
          thirdPartyBillingAccounts: foundThirdPartyBillingAccounts as HydratedThirdPartyBillingAccount[],
        }
      : {}),
    ...(tenant ? { tenant: extractTenantDetails(tenant) } : {}),
    validationInfo: {
      errors,
      warnings,
      configurationValid: !errors.length,
      configurationComplete: !errors.length && !warnings.length,
    },
  };
};
